import { Injectable } from '@angular/core';
import { HttpPromiseService } from './http-promise.service';
import { IClientActivityLog, IClientActivityLogResponse, LogLevel } from '../interfaces/client-activity-log.interface';

/**
 * Service for logging client-side activities to the backend
 * This service provides methods to track user actions, errors, and events
 */
@Injectable({
  providedIn: 'root'
})
export class ClientActivityLogService {
  
  constructor(private httpPromiseService: HttpPromiseService) {}

  /**
   * Log a client activity to the backend
   * @param activityLog - The activity log data to send
   * @returns Promise with the API response
   */
  async logActivity(activityLog: IClientActivityLog): Promise<IClientActivityLogResponse> {
    try {
      const response = await this.httpPromiseService.httpPostRequest(
        '/api/client-activity-logs',
        activityLog
      );
      return response;
    } catch (error) {
      // Silently fail to prevent logging errors from breaking the app
      console.error('Failed to log activity:', error);
      return { success: false, message: 'Failed to log activity' };
    }
  }

  /**
   * Log an error event
   * @param activityType - Type of activity (e.g., 'geocode_api_call', 'user_action')
   * @param message - Description of the error
   * @param data - Additional error data
   * @param page - Current page/route
   */
  async logError(activityType: string, message: string, data?: any, page?: string): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType,
      logLevel: 'error',
      message,
      data,
      page
    });
  }

  /**
   * Log a warning event
   * @param activityType - Type of activity
   * @param message - Description of the warning
   * @param data - Additional warning data
   * @param page - Current page/route
   */
  async logWarning(activityType: string, message: string, data?: any, page?: string): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType,
      logLevel: 'warn',
      message,
      data,
      page
    });
  }

  /**
   * Log an info event
   * @param activityType - Type of activity
   * @param message - Description of the event
   * @param data - Additional event data
   * @param page - Current page/route
   */
  async logInfo(activityType: string, message: string, data?: any, page?: string): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType,
      logLevel: 'info',
      message,
      data,
      page
    });
  }

  /**
   * Log a debug event
   * @param activityType - Type of activity
   * @param message - Description of the debug event
   * @param data - Additional debug data
   * @param page - Current page/route
   */
  async logDebug(activityType: string, message: string, data?: any, page?: string): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType,
      logLevel: 'debug',
      message,
      data,
      page
    });
  }

  /**
   * Log a trace event
   * @param activityType - Type of activity
   * @param message - Description of the trace event
   * @param data - Additional trace data
   * @param page - Current page/route
   */
  async logTrace(activityType: string, message: string, data?: any, page?: string): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType,
      logLevel: 'trace',
      message,
      data,
      page
    });
  }

  /**
   * Log API call activities (useful for tracking external API usage like Google Geocoding)
   * @param apiName - Name of the API being called
   * @param message - Description of the API call
   * @param data - Request/response data
   * @param page - Current page/route
   * @param logLevel - Optional log level (defaults to 'info')
   */
  async logApiCall(
    apiName: string, 
    message: string, 
    data?: any, 
    page?: string, 
    logLevel: LogLevel = 'info'
  ): Promise<IClientActivityLogResponse> {
    return this.logActivity({
      activityType: `api_call_${apiName}`,
      logLevel,
      message,
      data,
      page
    });
  }
}
